import { GetAnimationDataQuery } from "@/gql/generated/graphql";
import { FrameImage } from "@/lib/canvas/frame-image";
import config from "@/lib/config";

import { post } from "./fetch";
const UPLOAD_BATCH_SIZE = 5;

const loadFrame = function (
  animationUrl: string,
  frameId: number,
  version: number,
  width: number,
  height: number,
  schemaVersion: number,
): Promise<HTMLCanvasElement> {
  return new Promise(function (resolve, reject) {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    canvas.width = width;
    canvas.height = height;
    const img = document.createElement("img");
    img.crossOrigin = "Anonymous";
    img.addEventListener("load", function () {
      ctx.drawImage(img, 0, 0);
      resolve(canvas);
    });
    img.addEventListener("error", function () {
      return reject(new Error("Image failed to load"));
    });
    img.src = `${config.s3Path}/${animationUrl}/${
      schemaVersion > 2 ? `v${version}/` : ""
    }${frameId}.png`;
  });
};

export const save = function (
  url: string,
  images: string[],
  version: number,
  onFrameCompleted: (progress?: number) => void,
  abortSignal: AbortSignal,
) {
  return uploadAllFrames(url, images, version, onFrameCompleted, abortSignal);
};

export const loadFramesService = async function ({
  imagesToLoad,
  onFrameCompleted,
  animationData,
}: {
  imagesToLoad: string[];
  onFrameCompleted: () => void;
  animationData: NonNullable<GetAnimationDataQuery["animation"]>;
}) {
  const images = await Promise.all<FrameImage>(
    imagesToLoad.map(async (_, index) => {
      const canvas = await loadFrame(
        animationData.url,
        index,
        animationData.version ?? 0,
        animationData.width,
        animationData.height,
        animationData.schemaVersion,
      );
      onFrameCompleted();
      return new FrameImage({ from: canvas });
    }),
  );

  return { images };
};

export const uploadAllFrames = async function (
  animationUrl: string,
  images: string[],
  version: number,
  onFrameCompleted: (progress?: number) => void,
  abortSignal: AbortSignal,
) {
  for (
    let imageIndex = 0;
    imageIndex < images.length;
    imageIndex += UPLOAD_BATCH_SIZE
  ) {
    await uploadBatch({
      url: animationUrl,
      version,
      abortSignal,
      imageBatch: images
        .slice(imageIndex, imageIndex + UPLOAD_BATCH_SIZE)
        .map((imageData, offset) => {
          return {
            id: offset + imageIndex,
            imageData,
          };
        }),
    });
    onFrameCompleted(Math.ceil((imageIndex / images.length) * 100));
  }
};

export const cleanupService = async ({
  slug,
  currentVersion,
  abortSignal,
}: {
  slug: string;
  currentVersion: number;
  abortSignal: AbortSignal;
}) => {
  return await post(
    `${config.apiUrl}/api/animation/cleanup`,
    {
      slug,
      currentVersion,
    },
    { abortSignal },
  );
};

const uploadBatch = ({
  url,
  imageBatch,
  version,
  abortSignal,
}: {
  url: string;
  imageBatch: { id: number; imageData: string }[];
  version: number;
  abortSignal: AbortSignal;
}) => {
  return post(
    `${config.apiUrl}/api/animation/frame`,
    {
      animationUrl: url,
      version,
      images: imageBatch,
    },
    { abortSignal },
  );
};
